import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function AcceptJobDialog({ job }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [captainId, setCaptainId] = useState("");
  const [target, setTarget] = useState(job.target_completion_date || "");

  const { data: captains = [] } = useQuery({ queryKey: ["captains"], queryFn: async () => (await api.get("/captains")).data, enabled: open });

  const mutation = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${job.id}/accept`, {
      assigned_captain_id: captainId || null,
      target_completion_date: target || null,
    })).data,
    onSuccess: () => {
      toast.success("Job activated");
      qc.invalidateQueries({ queryKey: ["job", job.id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setOpen(false);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="accept-job-button"><CheckCircle2 className="h-4 w-4 mr-2" /> Accept &amp; Activate</Button>
      </DialogTrigger>
      <DialogContent data-testid="accept-job-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Accept job request</DialogTitle>
          <DialogDescription>Assign a captain and confirm the completion date to move this request into production.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <Label>Assign Captain</Label>
            <Select value={captainId} onValueChange={setCaptainId}>
              <SelectTrigger className="mt-1.5" data-testid="accept-captain-select"><SelectValue placeholder="Select captain" /></SelectTrigger>
              <SelectContent>
                {captains.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Completion date</Label>
            <Input type="date" value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1.5" data-testid="accept-target-date" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={mutation.isPending} data-testid="confirm-accept-button">
              {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Activate job
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
